import React, { useContext } from 'react';
import clsx from 'clsx';

// mobx context
import { observer } from 'mobx-react';

// @material-ui/core components
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import PersonalStoreContext from '../../stores/PersonalStore';

// core components
import HeaderSection from '../../components/header/HeaderSection';

// core utils
import {
  countries,
  civilStatusEs,
  civilStatusEn,
  professionEs,
  professionEn
} from '../../utils/Constants';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  gridComponent: {
    flexGrow: 1
  },
  gridPaper: {
    paddingLeft: '15px',
    paddingRight: '15px',
    textAlign: 'justify'
    // textAlign: 'center',
    // color: theme.palette.text.secondary,
  },
  gridLabel: {
    color: '#000',
    fontSize: '20px',
    lineHeight: '135%',
    width: '100%',
    wordBreak: 'break-word',
    margin: '0px',
    fontWeight: '600'
  },
  textField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 200
  },
  dense: {
    marginTop: 19
  },
  menu: {
    width: 200
  },
  root: {
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  icon: {
    borderRadius: '50%',
    width: 16,
    height: 16,
    boxShadow: 'inset 0 0 0 1px rgba(16,22,26,.2), inset 0 -1px 0 rgba(16,22,26,.1)',
    backgroundColor: '#f5f8fa',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.8),hsla(0,0%,100%,0))',
    '$root.Mui-focusVisible &': {
      outline: '2px auto rgba(19,124,189,.6)',
      outlineOffset: 2
    },
    'input:hover ~ &': {
      backgroundColor: '#ebf1f5'
    },
    'input:disabled ~ &': {
      boxShadow: 'none',
      background: 'rgba(206,217,224,.5)'
    }
  },
  checkedIcon: {
    backgroundColor: '#137cbd',
    backgroundImage: 'linear-gradient(180deg,hsla(0,0%,100%,.1),hsla(0,0%,100%,0))',
    '&:before': {
      display: 'block',
      width: 16,
      height: 16,
      backgroundImage: 'radial-gradient(#fff,#fff 28%,transparent 32%)',
      content: '""'
    },
    'input:hover ~ &': {
      backgroundColor: '#106ba3'
    }
  }
}));

function StyledRadio(props) {
  const classes = useStyles();

  return (
    <Radio
      className={classes.root}
      disableRipple
      color="default"
      checkedIcon={<span className={clsx(classes.icon, classes.checkedIcon)} />}
      icon={<span className={classes.icon} />}
      {...props}
    />
  );
}

const SectionPersonal = observer(({ lang }) => {
  const profession = lang.lang === 'es' ? professionEs : professionEn;
  const civilStatus = lang.lang === 'es' ? civilStatusEs : civilStatusEn;
  const classes = useStyles();
  const personalStore = useContext(PersonalStoreContext);

  const handleChange = event => {
    personalStore.setValue('valFullName', {
      error: false,
      message: ''
    });
    personalStore.setValue(event.target.name, event.target.value);
  };

  const handleChangeEmail = event => {
    personalStore.setValue('valEmail', {
      error: false,
      message: ''
    });
    personalStore.setValue(event.target.name, event.target.value);
  };

  return (
    <div>
      <HeaderSection tittle={lang.sectionPersonalTittle} />
      <div className={classes.gridComponent}>
        <Grid container spacing={3}>
          <Grid item md={6} xs={12}>
            <div className={classes.gridPaper}>
              <p className={classes.gridLabel}>{lang.sectionPersonalName}</p>
              <TextField
                id="name-txt"
                required
                placeholder="e.g: Carlos Rodriguez Velez"
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true
                }}
                name="name"
                value={personalStore.name}
                onChange={handleChange}
                error={personalStore.valFullName.error}
                helperText={personalStore.valFullName.message}
              />
            </div>
          </Grid>
          <Grid item md={6} />

          <Grid item md={6} xs={12}>
            <div className={classes.gridPaper}>
              <p className={classes.gridLabel}>{lang.personalEmail}</p>
              <TextField
                id="email-txt"
                required
                type="email"
                placeholder="correo@empresa.com"
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true
                }}
                name="email"
                value={personalStore.email}
                onChange={handleChangeEmail}
                error={personalStore.valEmail.error}
                helperText={personalStore.valEmail.message}
              />
            </div>
          </Grid>
          <Grid item md={6} />

          <Grid item md={6} xs={12}>
            <div className={classes.gridPaper}>
              <p className={classes.gridLabel}>{lang.personalPhone}</p>
              <TextField
                id="phone-txt"
                name="phone"
                value={personalStore.phone}
                onChange={handleChange}
                placeholder="6144-5668"
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true
                }}
              />
            </div>
          </Grid>
          <Grid item md={6} />

          <Grid item md={6} xs={12}>
            <div className={classes.gridPaper}>
              <p className={classes.gridLabel}>{lang.personalCountry}</p>
              <TextField
                id="country-sel"
                select
                name="country"
                value={personalStore.country}
                onChange={handleChange}
                SelectProps={{
                  MenuProps: {
                    className: classes.menu
                  }
                }}
                margin="normal"
                fullWidth
              >
                {countries.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          </Grid>
          <Grid item md={6} />

          <Grid item md={6} xs={12}>
            <div className={classes.gridPaper}>
              <p className={classes.gridLabel}>{lang.personalCivilStatus}</p>
              <br />
              <RadioGroup
                name="civilStatus"
                value={personalStore.civilStatus}
                onChange={handleChange}
                aria-label="single"
              >
                {civilStatus.map((row, index) => (
                  <FormControlLabel
                    key={index}
                    value={row.value}
                    control={<StyledRadio />}
                    label={row.label}
                  />
                ))}
              </RadioGroup>
            </div>
          </Grid>
          <Grid item md={6} />

          <Grid item md={6} xs={12}>
            <div className={classes.gridPaper}>
              <p className={classes.gridLabel}>{lang.personalDependents}</p>
              <br />
              <TextField
                id="standard-select-currency"
                select
                style={{
                  marginTop: '-5px',
                  marginRight: '10px'
                }}
                value={personalStore.haveDependent}
                onChange={event => personalStore.setValue('haveDependent', event.target.value)}
                SelectProps={{
                  MenuProps: {
                    className: classes.menu
                  }
                }}
                margin="normal"
              >
                <MenuItem key={1} value>
                  {lang.yes}
                </MenuItem>
                <MenuItem key={2} value={false}>
                  No
                </MenuItem>
              </TextField>
              {personalStore.haveDependent && (
                <>
                  <p style={{ marginBottom: '0px' }}>{lang.personalDependentsAmount}</p>
                  <TextField
                    id="dependents-txt"
                    name="dependents"
                    value={personalStore.dependentAmount}
                    onChange={e => personalStore.setValue('dependentAmount', e.target.value)}
                    placeholder="1"
                    margin="normal"
                    InputLabelProps={{
                      shrink: true
                    }}
                  />
                </>
              )}
            </div>
          </Grid>
          <Grid item md={6} />

          <Grid item md={6} xs={12}>
            <div className={classes.gridPaper}>
              <p className={classes.gridLabel}>{lang.personalProfession}</p>
              <br />

              <RadioGroup
                name="profession"
                value={personalStore.profession}
                onChange={handleChange}
                aria-label="businessman"
              >
                {profession.map((row, index) => (
                  <FormControlLabel
                    key={index}
                    value={row.value}
                    control={<StyledRadio />}
                    label={row.label}
                  />
                ))}
              </RadioGroup>
            </div>
          </Grid>
          <Grid item md={6} />
        </Grid>
      </div>
    </div>
  );
});

export default SectionPersonal;
